import * as React from 'react';

type EventHandler<TEvent> = (e: TEvent) => void;
const listeners: { [index: string]: Array<EventHandler<any>> } = {};

type Props = {
  className?: string;
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>;

// This component is needed to bind events to the most outer
// component but still use Reacts event system. If we were to
// bind directly to `document` you won't be able to stop the
// propagation to those handlers from inside React, since React
// only binds one handler to `document`.
export default (props: Props) => {
  const rootProps: Props = {
    ...props,
    onClick: e => {
      if (props.onClick) {
        props.onClick(e);
      }
      triggerEvent(e);
    },
    onKeyDown: e => {
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
      triggerEvent(e);
    },
  };

  return <div {...rootProps} />;
};

export type ScopeSetScrollEvent = {
  type: 'scope:setscroll';
  position: number;
};

export type ScopeFatalJsErrorEvent = {
  type: 'scope:fataljserror';
  error: Error;
  info: React.ErrorInfo;
};

type RootEvent =
  | 'click'
  | 'keydown'
  | 'resize'
  | 'scroll'
  | 'touchmove'
  | 'touchstart'
  | 'touchend'
  | 'scope:setscroll'
  | 'scope:fataljserror';

export function on(event: 'click', callback: EventHandler<React.MouseEvent<HTMLElement>>): () => void;
export function on(event: 'keydown', callback: EventHandler<React.KeyboardEvent<HTMLElement>>): () => void;
export function on(event: 'resize' | 'scroll', callback: EventHandler<Event>): () => void;
export function on(event: 'touchstart' | 'touchmove' | 'touchend', callback: EventHandler<TouchEvent>): () => void;
export function on(event: 'scope:setscroll', callback: EventHandler<ScopeSetScrollEvent>): () => void;
export function on(event: 'scope:fataljserror', callback: EventHandler<ScopeFatalJsErrorEvent>): () => void;

export function on<TEvent>(event: RootEvent, callback: EventHandler<TEvent>) {
  if (!__BROWSER__) {
    console.warn('Root.on should not be called on the server');
    return;
  }

  const events = event.split(' ');
  events.forEach(eventName => {
    if (!listeners[eventName]) {
      listeners[eventName] = [];
    }

    listeners[eventName].push(callback);
    handleWindowSubscription(eventName);
  });

  // We return a function here that you can call to unsubscribe to the event.
  // This instead of exporting an `off` function where you have to pass
  // the same function you passed to `on` which makes it impossible to
  // pass an inline function to `on`.
  return () =>
    events.forEach(eventName => {
      listeners[eventName] = listeners[eventName].filter(listener => listener !== callback);
      handleWindowSubscription(eventName);
    });
}

export function scrollPosition(newPosition?: number) {
  const element = document.scrollingElement || document.documentElement || document.body;

  if (newPosition !== undefined) {
    element.scrollTop = newPosition;

    triggerEvent({ type: 'scope:setscroll', position: newPosition } as ScopeSetScrollEvent);
  }

  return element.scrollTop;
}

const easeInOutQuad = (time: number, start: number, change: number, duration: number) => {
  time /= duration / 2;
  if (time < 1) {
    return (change / 2) * time * time + start;
  }
  time--;
  return (-change / 2) * (time * (time - 2) - 1) + start;
};

export function scrollTo(to: number, duration: number) {
  const startTime = Date.now();
  const startTop = scrollPosition();
  const distance = to - startTop;
  let userHasInteracted = false;

  const onUserInteraction = (e: Event) => {
    userHasInteracted = true;
  };

  const onScroll = (e: Event) => e.stopPropagation();

  return new Promise((resolve, reject) => {
    const scrollFrame = () => {
      const currentDate = Date.now();
      const currentTime = currentDate - startTime;
      scrollPosition(easeInOutQuad(currentTime, startTop, distance, duration));
      if (userHasInteracted) {
        return resolve();
      }
      if (currentTime < duration) {
        requestAnimationFrame(scrollFrame);
      } else {
        scrollPosition(to);
        return resolve();
      }
    };

    // We don't let anyone else notice the scroll events because
    // we want a smooth animation
    window.addEventListener('scroll', onScroll, true);
    requestAnimationFrame(scrollFrame);
    requestAnimationFrame(() => {
      // If a user initiated event occurs, stop the ongoing animation
      // We're putting this in a `rAF` so that the events are not bound
      // too early, causing the scroll to stop prematurely (especially true for touch devices)
      window.addEventListener('touchstart', onUserInteraction, false);
      window.addEventListener('wheel', onUserInteraction, false);
      window.addEventListener('click', onUserInteraction, false);
      window.addEventListener('keydown', onUserInteraction, false);
    });
  }).finally(() => {
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('touchstart', onUserInteraction, false);
    window.removeEventListener('wheel', onUserInteraction, false);
    window.removeEventListener('click', onUserInteraction, false);
    window.removeEventListener('keydown', onUserInteraction, false);
  });
}

export function one(event: 'click', callback: EventHandler<React.MouseEvent<HTMLElement>>): void;
export function one(event: 'keydown', callback: EventHandler<React.KeyboardEvent<HTMLElement>>): void;
export function one(event: 'resize' | 'scroll', callback: EventHandler<Event>): void;
export function one(event: 'touchstart' | 'touchmove' | 'touchend', callback: EventHandler<TouchEvent>): void;
export function one(event: 'scope:setscroll', callback: EventHandler<ScopeSetScrollEvent>): void;
export function one(event: 'scope:fataljserror', callback: EventHandler<ScopeFatalJsErrorEvent>): void;

export function one<TEvent>(event: RootEvent, callback: EventHandler<TEvent>) {
  if (!__BROWSER__) {
    console.warn('one should not be called during server-side rendering');
    return;
  }

  const unsubscribe = on(event as any, (e: any) => {
    callback(e);
    unsubscribe();
  });
}

export function offsetTop(element: HTMLElement): number {
  if (!element) {
    return 0;
  }
  return element.offsetTop + offsetTop(element.offsetParent as HTMLElement);
}

function hasSubscribers(eventName: string) {
  return listeners[eventName] && listeners[eventName].length > 0;
}

export function triggerEvent<T extends { type: string }>(e: T) {
  if (!hasSubscribers(e.type)) {
    return;
  }
  listeners[e.type].forEach(listener => {
    listener(e);
  });
}

const throttling: string[] = [];

// Some events than can fire at a high rate and execute computationally
// expensive operations such as DOM modifications. Those events should
// be throttled, such as `scroll` and `resize`.
function throttleEvent<T extends {type: string }>(e: T) {
  if (!hasSubscribers(e.type) || throttling.indexOf(e.type) !== -1) {
    return;
  }
  throttling.push(e.type);
  window.requestAnimationFrame(() => {
    triggerEvent(e);
    throttling.splice(throttling.indexOf(e.type), 1);
  });
}

const windowEvents = {
  resize: throttleEvent,
  scroll: throttleEvent,
  // We need `touchmove` on the window because you can't swipe out
  // the drawer/mini cart
  touchmove: throttleEvent,
  touchstart: throttleEvent,
  touchend: throttleEvent,
};

const boundWindowEvents: string[] = [];

// Pass `window` events defined in `windowEvents` to this module. Should be
// invoked when `listeners` has been manipulated
function handleWindowSubscription(eventName: string) {
  if (windowEvents[eventName as keyof typeof windowEvents]) {
    if (hasSubscribers(eventName) && !boundWindowEvents.includes(eventName)) {
      window.addEventListener(eventName, windowEvents[eventName as keyof typeof windowEvents]);
      boundWindowEvents.push(eventName);
    } else if (!hasSubscribers(eventName) && boundWindowEvents.includes(eventName)) {
      window.removeEventListener(eventName, windowEvents[eventName as keyof typeof windowEvents]);
      boundWindowEvents.splice(boundWindowEvents.indexOf(eventName), 1);
    }
  }
}
